import React from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { CSSTransitionGroup } from 'react-transition-group';
import fuzzyFilterFactory from 'react-fuzzy-filter';
import { formatDate } from '../../helpers';
import PaginatedList from '../../components/PaginatedList';
import ListFilterBox from '../../components/ListFilterBox';
import SelectDropdown from '../../components/SelectDropdown';

const ControlHeader = props => (
  <Row className="tools">
    <Col sm={4} className="table-header">
      <ListFilterBox
        onChange={props.filterBoxChange}
        fuzzyFilterFactory={props.fuzzyFilterFactory}
      />
    </Col>

    <Col sm={3} smOffset={5} className="table-header">
      {!props.hideAddButton && (
        <Button
          bsStyle="primary"
          type="submit"
          className="relative-padding"
          onClick={props.addButtonClick}
        >
          Assign
        </Button>
      )}
    </Col>
  </Row>
);

const AddNewRow = props => (
  <Row>
    <Col sm={4} className="table-entry">
      <SelectDropdown
        onChange={props.selectOnChange}
        value={props.selectValue}
        options={props.items.filter(i => !i.shareWithAllIndustries)}
        aria-label="Insight Select Box"
        focus={true}
      />
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        className="relative-padding"
        bsStyle="default"
        onClick={props.addItem}
      >
        Add
      </Button>
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        bsStyle="link"
        className="relative-padding"
        onClick={props.cancel}
      >
        Cancel
      </Button>
    </Col>
  </Row>
);

const LineItem = props => {
  const insightSet = props.item;
  return (
    <Row key={insightSet.id} className={insightSet.isArchived && 'archived'}>
      <Col sm={3} className="table-entry">
        {insightSet.name}
      </Col>
      <Col sm={3} className="table-entry" />
      <Col sm={2} className="table-entry">
        {formatDate(insightSet.dateCreated, true)}
      </Col>
      <Col sm={2} className="table-entry">
        {formatDate(insightSet.dateUpdated, true)}
      </Col>
      <Col sm={1} className="table-entry">
        {!insightSet.inheritedFromIndustry && (
          <Button
            className="action-button"
            bsStyle="link"
            onClick={() => props.removeItem(insightSet)}
            aria-label={`Remove ${insightSet.name}`}
          >
            Remove
          </Button>
        )}
      </Col>
    </Row>
  );
};

class CustomerInsightSets extends React.Component {
  static displayName = 'CustomerInsightSets';

  constructor(props) {
    super(props);
    this.state = {
      newInsightSetId: undefined,
      showNewInsightSet: false,
      filterValue: ''
    };
    this.fuzzyFilterFactory = fuzzyFilterFactory();
    this.timeoutId = undefined;
  }

  componentDidMount() {
    this.getInsightSets();
    this.screenReaderContent.focus();
  }

  componentWillUnmount() {
    // clear timeout to prevent setState on unmounted component
    clearTimeout(this.timeoutId);
  }

  getInsightSets = () => {
    const id = parseInt(this.props.params.customerId, 10);
    this.props.getCurrentOrganisationInsightSets(id, true);
    this.props.getCurrentOrganisationInsightSets(id, false);
  };

  removeInsightSet = insightSet => {
    this.setState({
      isSaving: true,
      errorMsg: undefined,
      successMsg: undefined
    });

    const organisationId = parseInt(this.props.params.customerId, 10);
    this.props
      .removeInsightSetFromCurrentOrganisation(insightSet, organisationId)
      .then(response => this.success(response), error => this.failure(error));
  };

  addInsightSet = () => {
    const newInsightSetId = this.state.newInsightSetId;
    if (newInsightSetId) {
      this.setState({
        isSaving: true,
        errorMsg: undefined,
        successMsg: undefined
      });

      const organisationId = parseInt(this.props.params.customerId, 10);
      this.props
        .addInsightSetToCurrentOrganisation(newInsightSetId, organisationId)
        .then(response => this.success(response), error => this.failure(error));
    }
    this.setState({ showNewInsightSet: false, newInsightSetId: undefined });
  };

  success = response => {
    this.setState({
      isSaving: false,
      errorMsg: undefined,
      successMsg: 'Success!'
    });
    this.timeoutId = setTimeout(
      () => this.setState({ successMsg: undefined }),
      3000
    );
  };

  failure = error => {
    this.setState({ isSaving: false, errorMsg: error.data.message });
  };

  updateFilterValue = filterValue => {
    this.setState({ filterValue: filterValue || '' });
  };

  addButtonClick = () => {
    this.setState({ showNewInsightSet: !this.state.showNewInsightSet });
  };

  cancel = () => {
    this.setState({ showNewInsightSet: false, newInsightSetId: undefined });
  };

  selectOnChange = newInsightSetId => {
    this.setState({ newInsightSetId });
  };

  render() {
    const {
      allocated,
      unallocated
    } = this.props.currentOrganisation.insightSets;
    const isGlobalUser = this.props.authentication.user.isInternalAdminUser;

    const LineItemWrapper = props => (
      <LineItem {...props} removeItem={this.removeInsightSet} />
    );

    return (
      <div>
        <Row />
        <h3
          className="screen-reader-only"
          tabIndex="-1"
          ref={i => (this.screenReaderContent = i)}
        >
          Showing insight sets for customer {this.props.currentCustomer.name}
        </h3>

        <ControlHeader
          filterBoxChange={this.updateFilterValue}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          addButtonClick={this.addButtonClick}
          hideAddButton={!isGlobalUser}
        />

        <Row>
          <Col sm={3} className="table-header">
            Insight Set name
          </Col>
          <Col sm={3} className="table-header">
            Managed by
          </Col>
          <Col sm={2} className="table-header">
            Date created
          </Col>
          <Col sm={2} className="table-header">
            Date updated
          </Col>
          <Col sm={1} className="table-header">
            Action
          </Col>
        </Row>
        <hr />

        {this.state.showNewInsightSet && (
          <AddNewRow
            items={unallocated}
            addItem={this.addInsightSet}
            selectValue={this.state.newInsightSetId}
            selectOnChange={this.selectOnChange}
            cancel={this.cancel}
          />
        )}

        <PaginatedList
          filterValue={this.state.filterValue}
          component={LineItemWrapper}
          items={allocated}
          showArchived={true}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          pluralName="insight sets"
        />
        <Row>
          <Col sm={12}>
            <CSSTransitionGroup
              transitionName="fade"
              transitionEnterTimeout={300}
              transitionLeaveTimeout={300}
            >
              {this.state.successMsg && (
                <p className="text-success">{this.state.successMsg}</p>
              )}
            </CSSTransitionGroup>
          </Col>
        </Row>
        {this.state.errorMsg && (
          <div className="alert alert-danger">{this.state.errorMsg}</div>
        )}
      </div>
    );
  }
}

export default CustomerInsightSets;



// WEBPACK FOOTER //
// ./src/containers/customer/CustomerInsightSets.js